/**
 * プロジェクトの枠: サイドバー（環境切替 + ナビ）と、環境を示す帯。
 *
 * WHY 環境をここまで目立たせるか: ゲームのバックエンドで一番多い事故は「本番のつもりで dev を触る /
 * dev のつもりで本番のプレイヤーを BAN する」。production では画面全体のアクセント色を朱に変え、
 * 斜線の警告帯を常に出す。URL にも環境を入れる（/p/:id/production/…）ので、リンクを共有しても取り違えない。
 */
import { useEffect } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { ENVS, envPath, type EnvName, type ProjectDetail } from '../api';
import { shortUrl, withUrlKey, type EnvUrls } from '../envWhere';
import { PrepaidBand } from '../components/PrepaidBand';
import { ErrorState, Spinner } from '../components/ui';
import { useResource } from '../hooks';
import { useT } from '../i18n';
import { isNavHidden } from '../launchHidden';
import { PROJECT_NAV, visibleNav } from '../projectNav';
import { navHiddenForRole } from '../projectMembers';
import { TopBar } from './ProjectsPage';

export interface ShellContext {
  project: ProjectDetail;
  env: EnvName;
  reloadProject: () => void;
  /** 両方のアプリの短い URL（neko-jump.kumodeck.app など）。文の中で「どのアプリか」を本物の URL で言うため（envWhere.ts・plain-words-3） */
  envUrls: EnvUrls;
}

export const useShell = () => useOutletContext<ShellContext>();

// 並びと見出しは projectNav.ts（芯 = Functions・ホスティングを上に・2026-09-30 console/core-first C1）。
// 最初の公開で見せない画面（launchHidden.ts）はそこで外す。項目自体は消さない: 一覧（LAUNCH_HIDDEN_FEATURES）から外せば元の場所に戻る
// 共同作業者（2026-10-22 core/handoff-1b）には持ち主だけの画面（鍵・お金・メール・ログインの設定・人に渡す）を出さない = 押して 403 owner_only を見せない。
// 役割はプロジェクトを読んでから分かるので、並びはコンポーネントの中で作る（読み込み中は持ち主の並び = 古いサーバーと同じ）
const navFor = (role: ProjectDetail['role']) => {
  const byRole = navHiddenForRole(role);
  return visibleNav(PROJECT_NAV, (to) => isNavHidden(to) || byRole(to));
};

function NavIcon({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" className="nav-icon">
      <path d={d} />
    </svg>
  );
}

export function ProjectShell() {
  const { projectId = '', env: envParam = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const env = (ENVS as string[]).includes(envParam) ? (envParam as EnvName) : null;
  const project = useResource<{ project: ProjectDetail }>(projectId ? `/v1/projects/${projectId}` : null);
  const { t } = useT();
  // 両方のアプリの URL（切り替え・帯・キーやパスワードの欄で「どのアプリか」を本物の URL で言う。2026-10-04 console/plain-words-3）。
  // WHY ここで 2 つとも取るか: 切り替えには両方の URL を並べる（今見ていない方の URL も要る）。hosting の応答は公開前でも url を返す
  const devHosting = useResource<{ url?: string | null }>(env && projectId ? `${envPath(projectId, 'development')}/hosting` : null);
  const prodHosting = useResource<{ url?: string | null }>(env && projectId ? `${envPath(projectId, 'production')}/hosting` : null);
  const envUrls: EnvUrls = { development: shortUrl(devHosting.data?.url), production: shortUrl(prodHosting.data?.url) };

  // スマホ幅ではナビが横スクロールの 1 行になる（最初の公開で 10 項目）。開いている項目が画面外にあると「今どこか」が分からないので、
  // ページが変わるたびに見える位置まで動かす（PC の縦のサイドバーでは既に見えているので何も起きない）
  useEffect(() => {
    document.querySelector<HTMLElement>('.sidebar .nav-link.is-active')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [location.pathname]);

  if (!env) return <Navigate to={`/p/${projectId}/development/overview`} replace />;
  const section = location.pathname.split('/')[4] ?? 'overview';
  // 帯の文:「公開中のアプリを操作しています（neko-jump.kumodeck.app）。…」。URL が取れない間・無いサーバーでは URL 無しの文
  const band = withUrlKey(`env.band.${env}` as const, envUrls[env] ?? null);
  const switchEnv = (next: EnvName) => navigate(`/p/${projectId}/${next}/${section}`);

  return (
    <div className={`page-wrap shell env-${env}`} data-env={env}>
      <a href="#main" className="skip-link">
        {t('common.skip')}
      </a>
      <TopBar />
      <div className="shell-body">
        <aside className="sidebar" aria-label={t('shell.navLabel')}>
          <div className="sidebar-project">
            <span className="sidebar-label">{t('shell.project')}</span>
            <strong className="sidebar-name">{project.data?.project.name ?? '…'}</strong>
            <span className="sidebar-slug">{project.data?.project.slug}</span>
          </div>

          <fieldset className="env-switch">
            <legend className="sidebar-label">{t('shell.environment')}</legend>
            {ENVS.map((e) => (
              <label key={e} className={`env-option env-option-${e} ${env === e ? 'is-active' : ''}`}>
                <input type="radio" name="env" value={e} checked={env === e} onChange={() => switchEnv(e)} />
                <span className="env-dot" aria-hidden="true" />
                <span className="env-option-text">
                  <span>{t(`env.${e}`)}</span>
                  {envUrls[e] && <span className="env-option-url">{envUrls[e]}</span>}
                </span>
              </label>
            ))}
          </fieldset>

          <nav className="nav">
            {navFor(project.data?.project.role).map((n) => (
              <div key={n.to} className="nav-item-wrap">
                {n.group && <span className="nav-group">{t(n.group)}</span>}
                <NavLink to={n.to} className={({ isActive }) => `nav-link ${isActive ? 'is-active' : ''}`}>
                  <NavIcon d={n.icon} />
                  <span>{t(n.label)}</span>
                </NavLink>
              </div>
            ))}
          </nav>
        </aside>

        <div className="shell-main">
          <div className={`env-band env-band-${env}`} role="status">
            <span className="env-band-tag">{t(`env.tag.${env}`)}</span>
            <span className="env-band-text">{t(band.key, band.vars)}</span>
          </div>
          {/* 前払い残高が 0 以下 = このゲームはプレイヤーに対して止まっている・デプロイは 402。概要・デプロイ・どの画面でも見えるように枠に置く */}
          <PrepaidBand />
          <main className="container" id="main" tabIndex={-1}>
            {project.loading && !project.data && <Spinner />}
            {project.error && <ErrorState error={project.error} what={t('shell.project')} onRetry={project.reload} />}
            {project.data && <Outlet context={{ project: project.data.project, env, reloadProject: project.reload, envUrls } satisfies ShellContext} />}
          </main>
        </div>
      </div>
    </div>
  );
}
